import { useCallback, useMemo, useState } from "react";
import { KeyRound, Lock, ShieldCheck } from "lucide-react";

import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { ClientInput } from "@/client/components/ClientInput";
import { ClientStatusMessage } from "@/client/components/ClientStatusMessage";
import {
  authorizeNativePairing,
  authorizeNativePairingByCode,
  loadNativePairingFromUrl,
  nativePairingPrefill,
  type NativePairing,
} from "@/client/lib/nativePairing";
import { createPasskey, signInWithPasskey, vaultUnlockPRFInputs } from "@/client/lib/passkeys";
import { saveWebSession } from "@/client/lib/session";

export function LoginPage() {
  const [nativePairing] = useState<NativePairing | null>(() => loadNativePairingFromUrl());
  const [prefill] = useState(() => nativePairingPrefill());
  const [username, setUsername] = useState(prefill.username);
  const [displayName, setDisplayName] = useState(prefill.displayName);
  const [pairingCode, setPairingCode] = useState(nativePairing?.userCode ?? "");
  const [busy, setBusy] = useState<null | "register" | "signin" | "pairing">(null);
  const [error, setError] = useState<string | null>(null);
  const [status, setStatus] = useState<string | null>(null);
  const [pairingAuthorized, setPairingAuthorized] = useState(false);
  const canSubmit = useMemo(() => username.trim().length > 0 && busy === null, [busy, username]);
  const activePairingCode = nativePairing?.userCode ?? pairingCode.trim();
  const hasDevicePairing = activePairingCode.length > 0;

  const authorizeDevicePairing = useCallback(
    async (sessionToken: string) => {
      const userCode = nativePairing?.userCode ?? pairingCode.trim();
      if (!userCode || pairingAuthorized) {
        return false;
      }

      setBusy("pairing");
      setError(null);
      try {
        const result = nativePairing
          ? await authorizeNativePairing({
              sessionToken,
              pairingId: nativePairing.pairingId,
              userCode,
            })
          : await authorizeNativePairingByCode({
              sessionToken,
              userCode,
            });
        setPairingAuthorized(true);
        setStatus(`Device sign-in authorized for ${result.account.username}. Return to the key.store app.`);
        return true;
      } catch (err) {
        setError(err instanceof Error ? err.message : "Failed to authorize device sign-in");
        return false;
      } finally {
        setBusy(null);
      }
    },
    [nativePairing, pairingAuthorized, pairingCode],
  );

  const onRegisterPasskey = useCallback(async () => {
    if (!canSubmit) {
      return;
    }

    const normalized = username.trim().toLowerCase();
    const name = displayName.trim() || normalized;

    setBusy("register");
    setError(null);
    setStatus(null);
    try {
      const finish = await createPasskey({
        username: normalized,
        displayName: name,
        prfInputs: vaultUnlockPRFInputs(normalized),
      });
      if (finish.session_token) {
        const next = {
          username: normalized,
          userId: finish.user_id,
          sessionToken: finish.session_token,
          signedInAt: new Date().toISOString(),
        };
        saveWebSession(next);
        if (hasDevicePairing) {
          setStatus("Passkey created. Authorizing device sign-in.");
          await authorizeDevicePairing(next.sessionToken);
        } else {
          setStatus(finish.passkey_prf?.available ? "Passkey created with PRF unlock. Open the app to continue." : "Passkey created. Open the app to continue.");
        }
      } else {
        setStatus(
          hasDevicePairing
            ? `Passkey created for ${normalized}. Sign in once to connect the device.`
            : `Passkey created for ${normalized}. Sign in to continue.`,
        );
      }
      setDisplayName("");
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to create passkey");
    } finally {
      setBusy(null);
    }
  }, [authorizeDevicePairing, canSubmit, displayName, hasDevicePairing, username]);

  const onSignIn = useCallback(async () => {
    if (!canSubmit) {
      return;
    }

    const normalized = username.trim().toLowerCase();

    setBusy("signin");
    setError(null);
    setStatus(null);
    try {
      const finish = await signInWithPasskey({
        username: normalized,
        prfInputs: vaultUnlockPRFInputs(normalized),
      });
      const next = {
        username: normalized,
        userId: finish.user_id,
        sessionToken: finish.session_token,
        signedInAt: new Date().toISOString(),
      };
      saveWebSession(next);
      if (hasDevicePairing) {
        setStatus("Signed in. Authorizing device sign-in.");
        await authorizeDevicePairing(next.sessionToken);
      } else {
        setStatus(finish.passkey_prf?.available ? "Signed in with PRF unlock. Open the app to continue." : "Signed in. Open the app to continue.");
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to sign in");
    } finally {
      setBusy(null);
    }
  }, [authorizeDevicePairing, canSubmit, hasDevicePairing, username]);

  return (
    <main className="min-h-[calc(100vh-4rem)] bg-[linear-gradient(to_bottom,hsl(var(--background)),hsl(var(--muted)))] px-4 py-8 sm:px-6 sm:py-12">
      <div className="mx-auto grid w-full max-w-5xl gap-5 lg:grid-cols-[1fr_360px]">
        <Card className="border-border/60 bg-card/80">
          <CardHeader className="pb-3">
            <div className="mb-3 flex h-10 w-10 items-center justify-center rounded-xl border border-border/70 bg-background/70">
              <KeyRound className="h-5 w-5" />
            </div>
            <CardTitle className="text-2xl tracking-tight">
              {hasDevicePairing ? "Connect device" : "Sign in with passkey"}
            </CardTitle>
            <CardDescription>
              Use your passkey to unlock key.store and finish device handoff.
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            {error ? <ClientStatusMessage variant="error" title="Error" message={error} /> : null}
            {status ? <ClientStatusMessage variant="success" title="Status" message={status} /> : null}
            {hasDevicePairing ? (
              <ClientStatusMessage
                variant={pairingAuthorized ? "success" : "info"}
                title={pairingAuthorized ? "Device connected" : "Device sign-in"}
                message={
                  pairingAuthorized
                    ? "The native key.store app can now finish sign-in."
                    : `Confirm code ${activePairingCode} after signing in with your passkey.`
                }
              />
            ) : null}

            <ClientInput
              id="keystore-login-email"
              label="Email"
              value={username}
              onChange={(event) => setUsername(event.target.value)}
              autoComplete="username webauthn"
              inputMode="email"
              placeholder="you@key.store"
            />

            <ClientInput
              id="keystore-login-display-name"
              label="Display name (optional)"
              value={displayName}
              onChange={(event) => setDisplayName(event.target.value)}
              autoComplete="name"
              placeholder="Avery"
            />

            {!nativePairing ? (
              <ClientInput
                id="keystore-login-device-code"
                label="Device code (optional)"
                value={pairingCode}
                onChange={(event) => setPairingCode(event.target.value)}
                autoComplete="one-time-code"
                inputMode="text"
                placeholder="ABCD2345"
              />
            ) : null}

            <div className="flex flex-wrap gap-2">
              <Button onClick={onSignIn} disabled={!canSubmit}>
                <KeyRound className="mr-2 h-4 w-4" />
                {busy === "signin" || busy === "pairing"
                  ? "Signing in..."
                  : hasDevicePairing
                    ? "Connect with passkey"
                    : "Sign in with passkey"}
              </Button>
              <Button variant="secondary" onClick={onRegisterPasskey} disabled={!canSubmit}>
                {busy === "register" ? "Creating..." : "Create passkey"}
              </Button>
            </div>
          </CardContent>
        </Card>

        <div className="space-y-4">
          <Card className="border-border/60 bg-background/60">
            <CardHeader className="pb-3">
              <CardTitle className="flex items-center gap-2 text-base">
                <ShieldCheck className="h-4 w-4" /> WebAuthn
              </CardTitle>
              <CardDescription>Passkey ceremonies stay on the secure key.store origin.</CardDescription>
            </CardHeader>
          </Card>

          <Card className="border-border/60 bg-background/60">
            <CardHeader className="pb-3">
              <CardTitle className="flex items-center gap-2 text-base">
                <Lock className="h-4 w-4" /> Device handoff
              </CardTitle>
              <CardDescription>Native clients return automatically after the code is confirmed.</CardDescription>
            </CardHeader>
          </Card>
        </div>
      </div>
    </main>
  );
}
